import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  AvatarGroup,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { User, Clock, CheckCircle, ListTodo } from 'lucide-react';

import { Project, Task, TaskStatus } from '@/types';

interface ProjectTeamTabProps {
  project: Project;
  tasks: Task[];
}

interface TeamMemberStats {
  userId: string;
  userName?: string;
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  totalTimeLogged: number;
  roles: string[];
}

/**
 * ProjectTeamTab
 * Team member overview with contribution stats
 */
const ProjectTeamTab: React.FC<ProjectTeamTabProps> = ({ project, tasks }) => {
  // Extract team members from RACI across all tasks
  const getTeamStats = (): TeamMemberStats[] => {
    const memberMap = new Map<string, TeamMemberStats>();

    tasks.forEach((task) => {
      // Process each RACI role
      const processRole = (userIds: string | string[], role: string, names?: string[]) => {
        const ids = Array.isArray(userIds) ? userIds : [userIds];
        const namesList = Array.isArray(names) ? names : names ? [names] : [];
        ids.forEach((userId, idx) => {
          if (!memberMap.has(userId)) {
            memberMap.set(userId, {
              userId,
              userName: namesList[idx] || undefined,
              totalTasks: 0,
              completedTasks: 0,
              inProgressTasks: 0,
              totalTimeLogged: 0,
              roles: [],
            });
          }
          const member = memberMap.get(userId)!;
          // Update userName if we got a name and don't have one yet
          if (!member.userName && namesList[idx]) {
            member.userName = namesList[idx];
          }
          member.totalTasks++;
          if (task.status === TaskStatus.COMPLETED) {
            member.completedTasks++;
          } else if (task.status === TaskStatus.IN_PROGRESS) {
            member.inProgressTasks++;
          }
          member.totalTimeLogged += (task.actualHours || 0) * 60;
          if (!member.roles.includes(role)) {
            member.roles.push(role);
          }
        });
      };

      processRole(task.raci.responsible, 'Responsible', task.raciNames?.responsible);
      processRole(task.raci.accountable, 'Accountable', task.raciNames?.accountable ? [task.raciNames.accountable] : undefined);
      processRole(task.raci.consulted, 'Consulted', task.raciNames?.consulted);
      processRole(task.raci.informed, 'Informed', task.raciNames?.informed);
    });

    return Array.from(memberMap.values()).sort(
      (a, b) => b.totalTasks - a.totalTasks
    );
  };

  const teamStats = getTeamStats();

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Responsible':
        return 'primary';
      case 'Accountable':
        return 'warning';
      case 'Consulted':
        return 'info';
      case 'Informed':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (teamStats.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <User size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No team members assigned
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create tasks and assign team members using RACI to see them here
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Team Overview */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Team Overview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
            <AvatarGroup max={10}>
              {teamStats.map((member) => (
                <Tooltip key={member.userId} title={member.userName || member.userId}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {(member.userName || member.userId).charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
            <Typography variant="body2" color="text.secondary">
              {teamStats.length} team member{teamStats.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Team Members Table */}
      <Card variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Team Member</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell width={150}>Tasks</TableCell>
                <TableCell width={150}>Progress</TableCell>
                <TableCell width={120}>Time Logged</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamStats.map((member) => {
                const progress =
                  member.totalTasks > 0
                    ? Math.round(
                        (member.completedTasks / member.totalTasks) * 100
                      )
                    : 0;

                return (
                  <TableRow key={member.userId}>
                    {/* Member */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {(member.userName || member.userId).charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {member.userName || member.userId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member.inProgressTasks} task
                            {member.inProgressTasks !== 1 ? 's' : ''} in progress
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Roles */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {member.roles.map((role) => (
                          <Chip
                            key={role}
                            label={role.charAt(0)}
                            size="small"
                            color={getRoleColor(role) as any}
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        ))}
                      </Box>
                    </TableCell>

                    {/* Tasks */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ListTodo size={16} style={{ color: '#64748b' }} />
                        <Typography variant="body2">
                          {member.completedTasks}/{member.totalTasks}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Progress */}
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 1,
                            bgcolor: 'grey.100',
                          }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 32 }}>
                          {progress}%
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Time Logged */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Clock size={16} style={{ color: '#f59e0b' }} />
                        <Typography variant="body2">
                          {formatDuration(member.totalTimeLogged)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Role Legend */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            RACI Role Legend
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label="R"
                size="small"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Responsible
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Does the work
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label="A"
                size="small"
                color="warning"
                sx={{ fontWeight: 600 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Accountable
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Owns the outcome
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label="C"
                size="small"
                color="info"
                sx={{ fontWeight: 600 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Consulted
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Provides input
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label="I"
                size="small"
                color="secondary"
                sx={{ fontWeight: 600 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Informed
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Kept updated
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectTeamTab;
